import ConsumableIcon from "@/components/icons/ConsumableIcon";
import CoolingIcon from "@/components/icons/CoolingIcon";
import ElectricityIcon from "@/components/icons/ElectricityIcon";
import HeatIcon from "@/components/icons/HeatIcon";
import { ResidualWasteIcon } from "@/components/icons/ResidualWasteIcon";
import WaterIcon from "@/components/icons/WaterIcon";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import List from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { Segmented } from "antd";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { SourceGroupBy, useFacilityImpactPerSource } from "../../hooks/useFacilityImpact";
import { formatAmountWithUnit } from "../../utils/formatters";
import { ImpactMetric } from "../../utils/types";

const step = 5;

export const facilityIcon = (type: string) => {
  if (type === "electricity") return <ElectricityIcon />;
  if (type === "water") return <WaterIcon />;
  if (type === "heat") return <HeatIcon />;
  if (type === "residual_waste") return <ResidualWasteIcon />;
  if (type === "cooling") return <CoolingIcon />;
  if (type === "consumable") return <ConsumableIcon />;
  return undefined;
};

export const facilityLabel = (type: string) => {
  if (type === "electricity") return "Electricity";
  if (type === "water") return "Water";
  if (type === "heat") return "Heat";
  if (type === "residual_waste") return "Residual waste";
  if (type === "cooling") return "Cooling";
  if (type === "consumable") return "Consumable";
  return type;
};

export default function FacilityImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();
  const translation = useTranslation();
  const [groupBy, setGroupBy] = useState<SourceGroupBy>("name");

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = useFacilityImpactPerSource({
    pagination: { page: 1, pageSize: limit + 5 },
    sort: {
      key: metric.key,
      order: "descend",
    },
    groupBy,
    metric: metric.key,
  });

  const numMore = Math.min((data?.sources.length || 0) - limit);

  const listData = data?.sources
    ? data?.sources.slice(0, limit).map((row) => {
        return {
          icon: facilityIcon(row.type),
          label: row.utility_source_label,
          value: row.impact[metric.key],
          extraProp: { label: "Amount", value: formatAmountWithUnit(row.amount, row.unit) },
          type:
            row.facilities.length === 1 ? (
              <CrossReference to={`/facilities/${row.facilities[0].id}`}>
                {row.facilities[0].name}
              </CrossReference>
            ) : (
              row.facilities.length + " facilities"
            ),
        };
      })
    : [];

  return (
    <Widget
      title={"Top contributing energy sources"}
      tooltip={translation["dashboard"]["facility"]["top_contributors"]}
      topRight={
        <Segmented
          value={groupBy}
          onChange={setGroupBy}
          options={[
            { label: "Unique", value: "id" },
            { label: "Grouped", value: "name" },
          ]}
        />
      }
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.total_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
